import { apiRequest } from '../lib/httpClient'
import { asArray, pickString } from './shared/coercion'
import type { HelpArticle } from '@/features/advisory-ai/assistant/types'

/**
 * Preppy help articles — see PREPPY-HELP-SPEC.md §3.
 *
 * `clientId` scopes nothing here; it is on the request so the permission filter
 * resolves feature access from the argument rather than the session's default
 * firm.
 *
 * `bodyHtml` is deliberately not sanitised here — that happens in
 * `ArticleReader.tsx`, next to the `dangerouslySetInnerHTML` it protects.
 */

const BASE = 'advisoryai/help/articles'

/** Reads a property under either casing, since the API mixes the two. */
function prop(source: unknown, name: string): unknown {
  if (!source || typeof source !== 'object') return undefined
  const record = source as Record<string, unknown>
  const pascal = name.charAt(0).toUpperCase() + name.slice(1)
  return record[name] ?? record[pascal]
}

function mapArticle(payload: unknown): HelpArticle {
  return {
    id: pickString(prop(payload, 'id')),
    title: pickString(prop(payload, 'title')),
    description: pickString(prop(payload, 'description')),
    url: pickString(prop(payload, 'url')),
    category: pickString(prop(payload, 'category')),
    bodyHtml: pickString(prop(payload, 'bodyHtml')),
    // Already ISO 8601 with an offset, unlike pop-ups — no UTC coercion needed.
    updatedAt: pickString(prop(payload, 'updatedAt')),
    minutes: pickString(prop(payload, 'minutes')),
  }
}

/**
 * Nothing re-sorts here: ordering is the server's, and the reader counts
 * "Article N of 10" from list position, so a client-side sort would renumber
 * what the panel says. Articles with no `id` are dropped — they cannot be opened.
 */
export async function fetchHelpArticles(clientId: string): Promise<HelpArticle[]> {
  const payload = await apiRequest<unknown>(BASE, {
    method: 'GET',
    query: { clientId },
  })

  return asArray<unknown>(prop(payload, 'articles')).map(mapArticle).filter((a) => a.id)
}
